import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import loadImage from '@99designs/apps/spa/pages/create/common/utils/loadImage';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import DesignerDetails from '@99designs/apps/spa/pages/create/components/DesignerDetails';
import LoadableImage from '@99designs/apps/spa/pages/create/components/LoadableImage/LoadableImage';
import Portal from '@99designs/apps/spa/pages/create/components/Portal';
import { breakpoints } from '@99designs/emu/settings';
import {
    FullscreenFrame,
    FullscreenHeader,
    FullscreenMain,
    FullscreenOverlay,
    FullscreenSidebar,
} from '@99designs/frontend-components/components/Fullscreen';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { IconButton } from '@99designs/frontend-components/components/IconButton';
import { IconX1 } from '@99designs/icons';
import { DesignPlaceholder } from '@99designs/spa-common';

interface Props {
    designer: DesignerCard_UserFragment;
    onClose: () => void;
    selectedDesignIndex: number;
    onDesignChange: (index: number) => void;
}

const DesignPlaceholderContainer = styled.div`
    @media screen and (min-width: ${breakpoints.normal[0]}px) {
        width: 200px;
        height: 200px;
        margin: auto;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
    }
`;

function FileOverlay(props: Props) {
    const [currentIndex, setCurrentIndex] = useState(props.selectedDesignIndex);

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        async function loadImages() {
            const { designs } = props.designer.profile;

            if (designs.length === 0) {
                return;
            }

            await loadImage(designs[currentIndex].imageSources.original).catch();
            for (let i = 0; i < designs.length; i++) {
                if (i !== currentIndex) {
                    loadImage(designs[i].imageSources.original).catch();
                }
            }
        }

        loadImages();

        if (containerRef.current) {
            containerRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.designer]);

    const hasPrev = () => currentIndex !== 0;

    const hasNext = () => currentIndex !== props.designer.profile.designs.length - 1;

    const prev = () => {
        if (hasPrev()) {
            const prevIndex = currentIndex - 1;
            setCurrentIndex(prevIndex);
            props.onDesignChange(prevIndex);
        }
    };

    const next = () => {
        if (hasNext()) {
            const nextIndex = currentIndex + 1;
            setCurrentIndex(nextIndex);
            props.onDesignChange(nextIndex);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.keyCode === 27) {
            event.stopPropagation();
            props.onClose();
        }

        if (event.keyCode === 37) {
            event.stopPropagation();
            prev();
        }

        if (event.keyCode === 39) {
            event.stopPropagation();
            next();
        }
    };

    const { designer, onClose } = props;
    const design = designer.profile.designs[currentIndex];

    return (
        <Portal>
            <FullscreenOverlay
                isClosing={false}
                isActive={true}
                hasOpeningAndClosingAnimation={false}
                isScrollableSmallScreens={true}
                enableScroll={false}
            >
                <div ref={containerRef} onKeyDown={handleKeyDown} tabIndex={0}>
                    <FullscreenFrame.Frame>
                        <FullscreenFrame.Main>
                            <FullscreenMain.Main hasHeader={true}>
                                <FullscreenMain.Header>
                                    <FullscreenHeader.Header variations={['design-details']}>
                                        {designer.profile.designs.length > 0 && (
                                            <div className="file-overlay__pagination">
                                                <div className="file-overlay__pagination__item">
                                                    <IconButton
                                                        variations={['on-white']}
                                                        isDisabled={!hasPrev()}
                                                        onClick={prev}
                                                    >
                                                        <Icon type={'left-2'} variations={['2x']} />
                                                    </IconButton>
                                                </div>
                                                <div className="file-overlay__pagination__item">
                                                    <strong>
                                                        {currentIndex + 1} /{' '}
                                                        {designer.profile.designs.length}
                                                    </strong>
                                                </div>
                                                <div className="file-overlay__pagination__item">
                                                    <IconButton
                                                        variations={['on-white']}
                                                        isDisabled={!hasNext()}
                                                        onClick={next}
                                                    >
                                                        <Icon
                                                            type={'right-2'}
                                                            variations={['2x']}
                                                        />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        )}
                                    </FullscreenHeader.Header>
                                </FullscreenMain.Header>
                                <FullscreenMain.Body variations={['design-details']}>
                                    {design ? (
                                        <LoadableImage
                                            url={design.imageSources.original}
                                            title={design.title ?? ''}
                                        />
                                    ) : (
                                        <DesignPlaceholderContainer>
                                            <DesignPlaceholder size="large" icon={'design-1'}>
                                                {__('No designs available')}
                                            </DesignPlaceholder>
                                        </DesignPlaceholderContainer>
                                    )}
                                </FullscreenMain.Body>
                            </FullscreenMain.Main>
                        </FullscreenFrame.Main>
                        <FullscreenFrame.Sidebar>
                            <FullscreenSidebar.Main>
                                <DesignerDetails designer={designer} onAddDesigner={onClose} />
                            </FullscreenSidebar.Main>
                        </FullscreenFrame.Sidebar>
                    </FullscreenFrame.Frame>
                </div>
                <div className="file-overlay__close-button">
                    <IconButton
                        onClick={onClose}
                        variations={['on-white']}
                        ariaLabel={__('Dismiss')}
                        dataTestId="FileOverlayDismissButton"
                    >
                        <IconX1 />
                    </IconButton>
                </div>
            </FullscreenOverlay>
        </Portal>
    );
}

export default FileOverlay;
